import React, { useEffect, useState } from 'react';
import { fetchCompanyInfo } from '../services/companyInfo';
import {
  Box, Container, Paper, Typography, Button, Stepper, Step,
  StepLabel, StepContent, Divider, Modal, IconButton,
} from '@mui/material';
import {
  LocalShipping, PhoneAndroid, ArrowBack, Security, Warning,
  CheckCircle, Download, Settings, FolderOpen, Close, TouchApp,
} from '@mui/icons-material';
import { QRCodeSVG } from 'qrcode.react';

const DRIVER_APP_URL = 'https://nara-logistics.kr/downloads/nara-driver.apk';
const IMG_BASE = '/uploads/install-guide';

// 이미지 컴포넌트 (터치하면 크게 보기)
function GuideImage({ src, alt, caption, onImageClick }) {
  return (
    <Box sx={{ textAlign: 'center', my: 2 }}>
      <Box
        onClick={() => onImageClick(src)}
        sx={{
          display: 'inline-block',
          borderRadius: 2,
          overflow: 'hidden',
          boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
          border: '1px solid #e0e0e0',
          cursor: 'pointer',
          transition: 'transform 0.2s',
          '&:hover': { transform: 'scale(1.02)' },
        }}
      >
        <img
          src={src}
          alt={alt}
          style={{ width: '100%', maxWidth: 260, height: 'auto', display: 'block' }}
        />
      </Box>
      {caption && (
        <Typography variant="caption" display="block" sx={{ mt: 0.8, color: 'text.secondary' }}>
          <TouchApp sx={{ fontSize: 14, mr: 0.3, verticalAlign: 'middle' }} />
          {caption}
        </Typography>
      )}
    </Box>
  );
}

// 2장 나란히 이미지 (before → after)
function GuideImagePair({ img1, img2, alt1, alt2, caption, onImageClick }) {
  return (
    <Box sx={{ my: 2 }}>
      <Box sx={{ display: 'flex', gap: 1.5, justifyContent: 'center', flexWrap: 'wrap' }}>
        {[{ src: img1, alt: alt1 }, { src: img2, alt: alt2 }].map((img, i) => (
          <Box
            key={i}
            onClick={() => onImageClick(img.src)}
            sx={{
              flex: '0 1 45%',
              minWidth: 140,
              borderRadius: 2,
              overflow: 'hidden',
              boxShadow: '0 2px 12px rgba(0,0,0,0.12)',
              border: '1px solid #e0e0e0',
              cursor: 'pointer',
              transition: 'transform 0.2s',
              '&:hover': { transform: 'scale(1.02)' },
            }}
          >
            <img
              src={img.src}
              alt={img.alt}
              style={{ width: '100%', height: 'auto', display: 'block' }}
            />
          </Box>
        ))}
      </Box>
      {caption && (
        <Typography variant="caption" display="block" sx={{ mt: 0.8, color: 'text.secondary', textAlign: 'center' }}>
          <TouchApp sx={{ fontSize: 14, mr: 0.3, verticalAlign: 'middle' }} />
          {caption}
        </Typography>
      )}
    </Box>
  );
}

export default function InstallGuidePage() {
  const [modalImage, setModalImage] = useState(null);
  const [company, setCompany] = useState({ company_name: '' });
  useEffect(() => { fetchCompanyInfo().then(setCompany).catch(() => {}); }, []);

  const openImage = (src) => setModalImage(src);
  const closeImage = () => setModalImage(null);

  return (
    <Box sx={{ minHeight: '100vh', background: 'linear-gradient(135deg, #1565c0 0%, #0d47a1 50%, #01579b 100%)' }}>
      {/* 헤더 */}
      <Box sx={{ py: 3, textAlign: 'center' }}>
        <LocalShipping sx={{ fontSize: 40, color: 'white', mb: 0.5 }} />
        <Typography variant="h4" fontWeight={700} color="white" gutterBottom>{company.company_name || ''}</Typography>
        <Typography variant="subtitle1" color="rgba(255,255,255,0.85)">운전기사 앱 설치 안내</Typography>
      </Box>

      <Container maxWidth="sm" sx={{ pb: 6 }}>
        {/* 뒤로가기 */}
        <Button
          href="/"
          startIcon={<ArrowBack />}
          sx={{ color: 'white', mb: 2, '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' } }}
        >
          메인으로 돌아가기
        </Button>

        {/* 매뉴얼 */}
        <Paper elevation={8} sx={{ borderRadius: 3, overflow: 'hidden', p: { xs: 2.5, sm: 4 } }}>
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 3 }}>
            <PhoneAndroid sx={{ fontSize: 32, color: '#1565c0', mr: 1.5 }} />
            <Box>
              <Typography variant="h5" fontWeight={700} color="#1565c0">앱 설치 방법</Typography>
              <Typography variant="body2" color="text.secondary">Android 기기 전용 (5단계)</Typography>
            </Box>
          </Box>

          <Divider sx={{ mb: 3 }} />

          <Stepper orientation="vertical" activeStep={-1}>

            {/* ===== 1단계: 다운로드 ===== */}
            <Step active expanded>
              <StepLabel StepIconProps={{ sx: { color: '#1565c0', '&.Mui-active': { color: '#1565c0' } } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
                  APK 파일 다운로드
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  아래 QR코드를 스캔하거나 다운로드 버튼을 눌러 설치 파일을 받으세요.
                </Typography>

                <Box sx={{ textAlign: 'center', my: 2 }}>
                  <Box sx={{
                    display: 'inline-block', p: 1.5, borderRadius: 2,
                    bgcolor: '#f5f5f5', boxShadow: '0 1px 6px rgba(0,0,0,0.08)', mb: 1.5,
                  }}>
                    <QRCodeSVG value={DRIVER_APP_URL} size={140} level="H" fgColor="#1565c0" />
                  </Box>
                  <Box>
                    <Button
                      variant="contained" size="medium" startIcon={<PhoneAndroid />}
                      href={DRIVER_APP_URL} target="_blank"
                      sx={{
                        py: 1, px: 3, fontWeight: 700, borderRadius: 2,
                        background: 'linear-gradient(135deg, #43a047 0%, #2e7d32 100%)',
                        '&:hover': { background: 'linear-gradient(135deg, #388e3c 0%, #1b5e20 100%)' },
                      }}
                    >
                      Android 앱 다운로드
                    </Button>
                  </Box>
                </Box>

                <Box sx={{ ml: 1 }}>
                  {['휴대폰 카메라 앱을 열어 QR코드를 스캔하세요.', '또는 [Android 앱 다운로드] 버튼을 직접 누르세요.', '다운로드가 시작되면 상단 알림창에서 진행 상태를 확인할 수 있습니다.'].map((t, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.8 }}>
                      <Typography variant="body2" sx={{ minWidth: 22, fontWeight: 700, color: '#1565c0' }}>{i + 1}.</Typography>
                      <Typography variant="body2">{t}</Typography>
                    </Box>
                  ))}
                </Box>
              </StepContent>
            </Step>

            {/* ===== 2단계: 권한 허용 ===== */}
            <Step active expanded>
              <StepLabel StepIconProps={{ sx: { color: '#1565c0', '&.Mui-active': { color: '#1565c0' } } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
                  출처를 알 수 없는 앱 설치 허용
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  처음 설치 시 "출처를 알 수 없는 앱 설치" 권한을 허용해야 합니다.
                </Typography>

                <GuideImagePair
                  img1={`${IMG_BASE}/step2-permission-off.jpg`}
                  img2={`${IMG_BASE}/step2-permission-on.jpg`}
                  alt1="권한 허용 전"
                  alt2="권한 허용 후"
                  caption="이미지를 터치하면 크게 볼 수 있습니다"
                  onImageClick={openImage}
                />

                <Box sx={{ ml: 1 }}>
                  {['"출처를 알 수 없는 앱 설치" 화면이 나타납니다.', '"내 파일" 항목의 [권한 허용] 토글을 켜주세요. (왼쪽 → 오른쪽)', '토글이 파란색으로 바뀌면 [업데이트] 또는 [설치]를 누르세요.'].map((t, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.8 }}>
                      <Typography variant="body2" sx={{ minWidth: 22, fontWeight: 700, color: '#1565c0' }}>{i + 1}.</Typography>
                      <Typography variant="body2">{t}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#e3f2fd', border: '1px solid #bbdefb' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Security sx={{ fontSize: 18, mr: 1, mt: 0.2, color: '#1565c0' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      이 경고는 Google Play 스토어가 아닌 외부에서 설치하는 모든 앱에 표시되는 안드로이드 기본 보안 설정입니다. 이 설정은 최초 1회만 필요합니다.
                    </Typography>
                  </Box>
                </Box>
              </StepContent>
            </Step>

            {/* ===== 3단계: 안전하지 않은 앱 경고 ===== */}
            <Step active expanded>
              <StepLabel StepIconProps={{ sx: { color: '#e65100', '&.Mui-active': { color: '#e65100' } } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
                  "안전하지 않은 앱" 경고 처리
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  "안전하지 않은 앱"이라는 경고가 나타나면 아래와 같이 진행하세요.
                </Typography>

                <GuideImagePair
                  img1={`${IMG_BASE}/step3-warning-popup.jpg`}
                  img2={`${IMG_BASE}/step3-warning-full.jpg`}
                  alt1="안전하지 않은 앱 팝업"
                  alt2="안전하지 않은 앱 전체 화면"
                  caption="이미지를 터치하면 크게 볼 수 있습니다"
                  onImageClick={openImage}
                />

                <Box sx={{ ml: 1 }}>
                  {['첫 번째 팝업: [무시하고 설치] 버튼을 누르세요.', '두 번째 화면: 하단의 [무시하고 설치] 버튼을 누르세요.'].map((t, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.8 }}>
                      <Typography variant="body2" sx={{ minWidth: 22, fontWeight: 700, color: '#e65100' }}>{i + 1}.</Typography>
                      <Typography variant="body2">{t}</Typography>
                    </Box>
                  ))}
                </Box>

                <Box sx={{ mt: 1.5, p: 1.5, borderRadius: 2, bgcolor: '#fff3e0', border: '1px solid #ffe0b2' }}>
                  <Box sx={{ display: 'flex', alignItems: 'flex-start' }}>
                    <Security sx={{ fontSize: 18, mr: 1, mt: 0.2, color: '#e65100' }} />
                    <Typography variant="caption" color="text.secondary" sx={{ lineHeight: 1.6 }}>
                      이 경고는 Google Play에 등록되지 않은 앱에 표시되는 기본 보안 알림입니다. {company.company_name ? `${company.company_name} ` : ''}앱은 안전하며, 개인정보를 수집하지 않습니다.
                    </Typography>
                  </Box>
                </Box>
              </StepContent>
            </Step>

            {/* ===== 4단계: 설치 완료 ===== */}
            <Step active expanded>
              <StepLabel StepIconProps={{ sx: { color: '#1565c0', '&.Mui-active': { color: '#1565c0' } } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
                  설치 완료
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  설치가 완료되면 아래와 같은 화면이 나타납니다.
                </Typography>

                <GuideImage
                  src={`${IMG_BASE}/step4-install-complete.jpg`}
                  alt="앱 설치 완료"
                  caption="이미지를 터치하면 크게 볼 수 있습니다"
                  onImageClick={openImage}
                />

                <Box sx={{ ml: 1 }}>
                  {[`"${company.company_name ? company.company_name + ' - ' : ''}앱이 설치되었습니다" 메시지가 표시됩니다.`, '[열기] 버튼을 눌러 앱을 바로 실행할 수 있습니다.', '[완료] 버튼을 누르면 나중에 홈 화면에서 실행할 수 있습니다.'].map((t, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.8 }}>
                      <Typography variant="body2" sx={{ minWidth: 22, fontWeight: 700, color: '#1565c0' }}>{i + 1}.</Typography>
                      <Typography variant="body2">{t}</Typography>
                    </Box>
                  ))}
                </Box>
              </StepContent>
            </Step>

            {/* ===== 5단계: 앱 실행 ===== */}
            <Step active expanded>
              <StepLabel StepIconProps={{ sx: { color: '#2e7d32', '&.Mui-active': { color: '#2e7d32' } } }}>
                <Typography variant="subtitle1" fontWeight={700} sx={{ fontSize: '1.05rem' }}>
                  앱 실행 및 기사 등록
                </Typography>
              </StepLabel>
              <StepContent>
                <Typography variant="body2" color="text.secondary" sx={{ mb: 1.5 }}>
                  앱 설치가 완료되었습니다! 앱을 열고 운전기사 등록을 진행하세요.
                </Typography>

                <Box sx={{ ml: 1 }}>
                  {['앱을 실행하면 로그인 화면이 나타납니다.', '처음 사용 시 [회원가입]을 눌러 기사 등록을 해주세요.', '등록 후 관리자 승인이 완료되면 배차를 받을 수 있습니다.'].map((t, i) => (
                    <Box key={i} sx={{ display: 'flex', alignItems: 'flex-start', mb: 0.8 }}>
                      <Typography variant="body2" sx={{ minWidth: 22, fontWeight: 700, color: '#2e7d32' }}>{i + 1}.</Typography>
                      <Typography variant="body2">{t}</Typography>
                    </Box>
                  ))}
                </Box>
              </StepContent>
            </Step>

          </Stepper>

          <Divider sx={{ my: 3 }} />

          {/* 문의 안내 */}
          <Box sx={{ textAlign: 'center', p: 2, bgcolor: '#f8f9fa', borderRadius: 2 }}>
            <Typography variant="body2" fontWeight={600} gutterBottom>
              설치에 어려움이 있으신가요?
            </Typography>
            <Typography variant="body2" color="text.secondary">
              고객센터 <strong>{company.company_phone || '1522-2451'}</strong>로 연락해 주세요.
            </Typography>
          </Box>
        </Paper>

        {/* 하단 정보 */}
        <Box sx={{ mt: 3, textAlign: 'center' }}>
          <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.3 }}>
            상호: {company.company_name || ''}
            {company.ceo_name ? ` | 대표: ${company.ceo_name}` : ''}
            {company.business_number ? ` | 사업자번호: ${company.business_number}` : ''}
          </Typography>
          {company.company_address && (
            <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.6)', mb: 0.3 }}>
              주소: {company.company_address}
            </Typography>
          )}
          {company.company_phone && (
            <Typography variant="caption" display="block" sx={{ color: 'rgba(255,255,255,0.6)' }}>
              고객센터: {company.company_phone}
            </Typography>
          )}
        </Box>
      </Container>

      {/* 이미지 확대 모달 */}
      <Modal open={!!modalImage} onClose={closeImage}>
        <Box
          onClick={closeImage}
          sx={{
            display: 'flex', alignItems: 'center', justifyContent: 'center',
            height: '100vh', bgcolor: 'rgba(0,0,0,0.85)', p: 2,
          }}
        >
          <IconButton
            onClick={closeImage}
            sx={{ position: 'absolute', top: 16, right: 16, color: 'white' }}
          >
            <Close sx={{ fontSize: 32 }} />
          </IconButton>
          {modalImage && (
            <img
              src={modalImage}
              alt="확대 보기"
              style={{
                maxWidth: '90%', maxHeight: '90vh',
                borderRadius: 8, boxShadow: '0 4px 20px rgba(0,0,0,0.5)',
              }}
            />
          )}
        </Box>
      </Modal>
    </Box>
  );
}
